import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardBody,
  Table,
  Row,
  Col,
  Container,
  Input,
  InputGroup,
  InputGroupText,
  Button,
  Spinner,
  FormGroup,
  Label,
} from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch,
  FaExternalLinkAlt,
  FaWallet,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { SiEthereum, SiTether } from "react-icons/si";
import UserHeader from "components/Headers/UserHeader.js";

const theme = {
  bgPage: "#251d0c",
  bgCard: "#18181c",
  cardBorder: "rgba(255, 176, 0, 0.12)",
  accent: "#FFB000",
  text: "#f4f4f5",
  textMuted: "#71717a",
  success: "#22c55e",
  danger: "#ef4444",
  eth: "#627eea",
  usdt: "#26a17b",
  inputBg: "#1f1f24",
  border: "rgba(255,255,255,0.08)",
};

const CONTENT_MAX_WIDTH = 1200;
const SPACING = 24;

const TreasuryActivity = () => {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [trackedFilter, setTrackedFilter] = useState("all");

  const getToken = () => localStorage.getItem("token");

  const authFetch = useCallback(async (url, options = {}) => {
    const token = getToken();
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...options.headers,
    };
    const response = await fetch(url, { ...options, headers });

    if (response.status === 401 || response.status === 403) {
      localStorage.removeItem("token");
      window.location.href = "/auth/login";
      throw new Error("Authentication failed");
    }

    const text = await response.text();
    if (!response.ok) {
      let message = `HTTP error! status: ${response.status}`;
      try {
        const json = JSON.parse(text);
        message = json.message || message;
      } catch (_) {
        if (text) message = text;
      }
      throw new Error(message);
    }

    return text ? JSON.parse(text) : {};
  }, []);

  const fetchActivity = useCallback(async () => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (search.trim()) params.append("search", search.trim());

    try {
      const result = await authFetch(
        `${process.env.REACT_APP_API_URL}/treasury-activity?${params}`,
      );
      setData(result);
    } catch (error) {
      toast.error(error.message || "Failed to load wallet activity");
      setData(null);
    }
  }, [page, limit, search, authFetch]);

  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    fetchActivity().finally(() => setLoading(false));
  }, [fetchActivity]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPage(1);
  };

  const formatSummaryValue = (val) => {
    if (val == null || val === "") return "0";
    const n = Number(val);
    if (Number.isNaN(n)) return String(val);
    if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
    if (n >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
    if (Number.isInteger(n)) return n.toLocaleString();
    return n.toLocaleString(undefined, {
      maximumFractionDigits: 4,
      minimumFractionDigits: 0,
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "—";
    return new Date(timestamp * 1000).toLocaleString();
  };

  const shortAddress = (addr) => {
    if (!addr || addr.length < 12) return addr;
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const etherscanTxUrl = (hash) => `https://etherscan.io/tx/${hash}`;
  const etherscanAddressUrl = (address) =>
    `https://etherscan.io/address/${address}`;

  const pagination = data?.pagination || {};
  const totalPages = pagination.totalPages || 1;
  const activity = data?.activity || [];
  const summary = data?.summary || {};
  const normalizeWallet = (value) =>
    typeof value === "string" ? value.trim().toLowerCase() : "";
  const trackedContractAddresses =
    data?.contractAnalytics?.trackedContractAddresses ||
    data?.trackedContractAddresses ||
    [];
  const trackedWallets = [data?.treasuryWallet, ...trackedContractAddresses].filter(
    (wallet, index, wallets) => {
      const normalizedWallet = normalizeWallet(wallet);
      if (!normalizedWallet) return false;
      return (
        wallets.findIndex(
          (candidate) => normalizeWallet(candidate) === normalizedWallet,
        ) === index
      );
    },
  );
  const treasuryWalletNormalized = normalizeWallet(data?.treasuryWallet);
  const trackedContractSet = new Set(
    trackedContractAddresses.map(normalizeWallet).filter(Boolean),
  );

  const getTrackedWalletLabel = (item) => {
    const fromNormalized = normalizeWallet(item?.from);
    const toNormalized = normalizeWallet(item?.to);
    const touchesTreasury =
      Boolean(treasuryWalletNormalized) &&
      (fromNormalized === treasuryWalletNormalized ||
        toNormalized === treasuryWalletNormalized);
    const touchesContract =
      trackedContractSet.has(fromNormalized) || trackedContractSet.has(toNormalized);

    if (touchesTreasury && touchesContract) return "Treasury + Contract";
    if (touchesTreasury) return "Treasury";
    if (touchesContract) return "Contract";
    return "—";
  };
  const filteredActivity = activity.filter((item) => {
    const label = getTrackedWalletLabel(item);
    if (trackedFilter === "all") return true;
    if (trackedFilter === "treasury") return label === "Treasury";
    if (trackedFilter === "contract") return label === "Contract";
    if (trackedFilter === "both") return label === "Treasury + Contract";
    return true;
  });

  const thStyle = {
    padding: "14px 24px",
    border: 0,
    color: theme.textMuted,
    fontSize: "0.6875rem",
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  };

  const tdStyle = {
    padding: "16px 24px",
    borderBottom: `1px solid ${theme.border}`,
  };

  return (
    <>
      <ToastContainer />
      <UserHeader />
      <Container
        fluid
        className="mt--7"
        style={{ background: theme.bgPage, paddingBottom: 48 }}
      >
        <div
          style={{
            maxWidth: CONTENT_MAX_WIDTH,
            margin: "0 auto",
            padding: `0 ${SPACING}px`,
          }}
        >
          {/* Subtitle */}
          <p
            className="mb-0"
            style={{
              color: theme.textMuted,
              fontSize: "0.9375rem",
              marginBottom: SPACING + 4,
            }}
          >
            Transaction history for the treasury wallet
          </p>

          {/* Stats row */}
          <Row className="g-4" style={{ marginBottom: SPACING }}>
            <Col xs="12" sm="6" xl="3">
              <Card
                className="border-0 h-100"
                style={{
                  background: theme.bgCard,
                  border: `1px solid ${theme.cardBorder}`,
                  borderRadius: 12,
                }}
              >
                <CardBody style={{ padding: SPACING }}>
                  <div
                    className="d-flex align-items-center"
                    style={{ gap: 14 }}
                  >
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: 44,
                        height: 44,
                        background: "rgba(255,176,0,0.12)",
                      }}
                    >
                      <FaWallet size={20} style={{ color: theme.accent }} />
                    </div>
                    <div className="min-w-0">
                      <div
                        style={{
                          color: theme.textMuted,
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                        }}
                      >
                        Treasury wallet
                      </div>
                      <div className="mt-2 d-flex flex-column" style={{ gap: 8 }}>
                        {trackedWallets.length === 0 ? (
                          <div
                            className="font-monospace text-break small"
                            style={{ color: theme.text }}
                          >
                            —
                          </div>
                        ) : (
                          trackedWallets.map((wallet) => (
                            <a
                              key={wallet}
                              href={etherscanAddressUrl(wallet)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="d-inline-flex align-items-center text-decoration-none font-monospace text-break small"
                              style={{ color: theme.text, gap: 6 }}
                              title={wallet}
                            >
                              {shortAddress(wallet)}
                              <FaExternalLinkAlt
                                style={{ fontSize: "0.65rem", opacity: 0.8 }}
                              />
                            </a>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xs="12" sm="6" xl="3">
              <Card
                className="border-0 h-100"
                style={{
                  background: theme.bgCard,
                  border: `1px solid ${theme.cardBorder}`,
                  borderRadius: 12,
                }}
              >
                <CardBody style={{ padding: SPACING }}>
                  <div
                    className="d-flex align-items-center"
                    style={{ gap: 14 }}
                  >
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: 44,
                        height: 44,
                        background: "rgba(98,126,234,0.15)",
                      }}
                    >
                      <SiEthereum size={22} style={{ color: theme.eth }} />
                    </div>
                    <div className="min-w-0">
                      <div
                        style={{
                          color: theme.textMuted,
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                        }}
                      >
                        ETH
                      </div>
                      <div className="mt-2" style={{ color: theme.text }}>
                        <span className="fw-bold">
                          {formatSummaryValue(summary.eth?.count ?? 0)}
                        </span>
                        <span
                          className="small ms-2"
                          style={{ color: theme.textMuted }}
                        >
                          txs
                        </span>
                      </div>
                      <div
                        className="small mt-1"
                        style={{ color: theme.textMuted }}
                      >
                        {formatSummaryValue(summary.eth?.totalReceived ?? 0)}{" "}
                        ETH received
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xs="12" sm="6" xl="3">
              <Card
                className="border-0 h-100"
                style={{
                  background: theme.bgCard,
                  border: `1px solid ${theme.cardBorder}`,
                  borderRadius: 12,
                }}
              >
                <CardBody style={{ padding: SPACING }}>
                  <div
                    className="d-flex align-items-center"
                    style={{ gap: 14 }}
                  >
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: 44,
                        height: 44,
                        background: "rgba(38,161,123,0.15)",
                      }}
                    >
                      <SiTether size={22} style={{ color: theme.usdt }} />
                    </div>
                    <div className="min-w-0">
                      <div
                        style={{
                          color: theme.textMuted,
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                        }}
                      >
                        USDT
                      </div>
                      <div className="mt-2" style={{ color: theme.text }}>
                        <span className="fw-bold">
                          {formatSummaryValue(summary.usdt?.count ?? 0)}
                        </span>
                        <span
                          className="small ms-2"
                          style={{ color: theme.textMuted }}
                        >
                          txs
                        </span>
                      </div>
                      <div
                        className="small mt-1"
                        style={{ color: theme.textMuted }}
                      >
                        {formatSummaryValue(summary.usdt?.totalReceived ?? 0)}{" "}
                        USDT received
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Activity table card */}
          <Card
            className="border-0"
            style={{
              background: theme.bgCard,
              border: `1px solid ${theme.cardBorder}`,
              borderRadius: 12,
              overflow: "hidden",
            }}
          >
            {/* Toolbar */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 20,
                padding: SPACING,
                borderBottom: `1px solid ${theme.border}`,
              }}
            >
              <div>
                <h6
                  className="mb-2"
                  style={{
                    color: theme.text,
                    fontWeight: 600,
                    fontSize: "1rem",
                  }}
                >
                  Activity
                </h6>
                <p className="mb-0 small" style={{ color: theme.textMuted }}>
                  {pagination.total != null
                    ? `${pagination.total} transaction${pagination.total !== 1 ? "s" : ""}`
                    : "Transaction list"}
                </p>
              </div>
              <div
                className="d-flex flex-wrap align-items-center"
                style={{ gap: 16 }}
              >
                <form onSubmit={handleSearchSubmit} className="d-flex">
                  <InputGroup
                    style={{
                      borderRadius: 8,
                      border: `1px solid ${theme.border}`,
                      overflow: "hidden",
                      background: theme.inputBg,
                      width: "auto",
                      minWidth: 260,
                    }}
                  >
                    <InputGroupText
                      style={{
                        background: "transparent",
                        border: "none",
                        color: theme.textMuted,
                        padding: "0.5rem 0.875rem",
                      }}
                    >
                      <FaSearch size={14} className="me-2" />
                    </InputGroupText>
                    <Input
                      placeholder="Tx hash or address..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      style={{
                        background: "transparent",
                        color: theme.text,
                        border: "none",
                        fontSize: "0.875rem",
                        padding: "0.5rem 0.5rem 0.5rem 0",
                        width: 160,
                      }}
                    />
                    
                    
                    {searchInput ? (
                      <Button
                        type="button"
                        color="link"
                        className="p-2"
                        onClick={clearSearch}
                        style={{ color: theme.textMuted }}
                        title="Clear"
                      >
                        <FaTimes size={12} />
                      </Button>
                    ) : null}
                    <Button
                      type="submit"
                      style={{
                        background: theme.accent,
                        color: "#0f0f12",
                        border: "none",
                        fontWeight: 600,
                        padding: "0.5rem 1rem",
                        fontSize: "0.875rem",
                      }}
                    >
                      Search
                    </Button>
                  </InputGroup>
                </form>
                <FormGroup
                  className="mb-0 d-flex align-items-center"
                  style={{ gap: 12 }}
                >
                  <Label
                    className="mb-0 small"
                    style={{ color: theme.textMuted }}
                  >
                    Tracked
                  </Label>
                  <Input
                    type="select"
                    value={trackedFilter}
                    onChange={(e) => {
                      setTrackedFilter(e.target.value);
                      setPage(1);
                    }}
                    style={{
                      width: 160,
                      background: theme.inputBg,
                      color: theme.text,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 8,
                      padding: "0.4rem 0.5rem",
                      fontSize: "0.875rem",
                    }}
                  >
                    <option value="all">All</option>
                    <option value="treasury">Treasury</option>
                    <option value="contract">Contract</option>
                  </Input>
                  <Label
                    className="mb-0 small"
                    style={{ color: theme.textMuted }}
                  >
                    Per page
                  </Label>
                  <Input
                    type="select"
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setPage(1);
                    }}
                    style={{
                      width: 72,
                      background: theme.inputBg,
                      color: theme.text,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 8,
                      padding: "0.4rem 0.5rem",
                      fontSize: "0.875rem",
                    }}
                  >
                    {[10, 20, 50, 100].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </div>
            </div>

            {loading ? (
              <div className="d-flex flex-column align-items-center justify-content-center py-5">
                <Spinner
                  style={{
                    color: theme.accent,
                    width: "2.5rem",
                    height: "2.5rem",
                  }}
                />
                <div className="mt-3 small" style={{ color: theme.textMuted }}>
                  Loading activity…
                </div>
              </div>
            ) : filteredActivity.length === 0 ? (
              <div
                className="text-center py-5"
                style={{
                  color: theme.textMuted,
                  fontSize: "0.9375rem",
                  borderTop: `1px solid ${theme.border}`,
                }}
              >
                No activity found for selected filter.
                {search && (
                  <div className="mt-2">
                    <Button
                      color="link"
                      className="p-0"
                      style={{ color: theme.accent, fontSize: "0.875rem" }}
                      onClick={clearSearch}
                    >
                      Clear search
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <Table
                    className="align-middle mb-0"
                    style={{ color: theme.text }}
                  >
                    <thead>
                      <tr style={{ background: "rgba(0,0,0,0.25)" }}>
                        <th style={thStyle}>Type</th>
                        <th style={thStyle}>Tracked Wallet</th>
                        <th style={thStyle}>Hash</th>
                        <th style={thStyle}>From</th>
                        <th style={thStyle}>To</th>
                        <th style={thStyle}>Value</th>
                        <th style={thStyle}>Date</th>
                        <th style={thStyle}>Block</th>
                        <th style={thStyle}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredActivity.map((item, idx) => (
                        <tr
                          key={item.hash || idx}
                          className="treasury-activity-row"
                        >
                          <td style={tdStyle}>
                            <span
                              style={{
                                display: "inline-block",
                                background:
                                  item.type === "ETH"
                                    ? "rgba(98,126,234,0.2)"
                                    : "rgba(38,161,123,0.2)",
                                color:
                                  item.type === "ETH" ? theme.eth : theme.usdt,
                                fontWeight: 600,
                                padding: "0.3rem 0.6rem",
                                borderRadius: 8,
                                fontSize: "0.8125rem",
                              }}
                            >
                              {item.type || "—"}
                            </span>
                          </td>
                          <td style={tdStyle}>
                            <span style={{ color: theme.textMuted, fontSize: "0.8125rem" }}>
                              {getTrackedWalletLabel(item)}
                            </span>
                          </td>
                          <td style={tdStyle}>
                            <a
                              href={etherscanTxUrl(item.hash)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="d-inline-flex align-items-center text-decoration-none"
                              style={{ color: theme.accent, gap: 6 }}
                            >
                              {shortAddress(item.hash)}
                              <FaExternalLinkAlt
                                style={{ fontSize: "0.65rem", opacity: 0.8 }}
                              />
                            </a>
                          </td>
                          <td
                            className="font-monospace small"
                            style={tdStyle}
                            title={item.from}
                          >
                            {shortAddress(item.from)}
                          </td>
                          <td
                            className="font-monospace small"
                            style={tdStyle}
                            title={item.to}
                          >
                            {shortAddress(item.to)}
                          </td>
                          <td style={tdStyle}>
                            <span style={{ color: theme.text }}>
                              {item.valueFormatted ?? item.value}
                            </span>
                            {item.tokenSymbol && (
                              <span
                                className="small ms-2"
                                style={{ color: theme.textMuted }}
                              >
                                {item.tokenSymbol}
                              </span>
                            )}
                          </td>
                          <td
                            className="small"
                            style={{ ...tdStyle, color: theme.textMuted }}
                          >
                            {formatDate(item.timestamp)}
                          </td>
                          <td
                            className="small"
                            style={{ ...tdStyle, color: theme.textMuted }}
                          >
                            {item.blockNumber ?? "—"}
                          </td>
                          <td style={tdStyle}>
                            <span
                              style={{
                                display: "inline-block",
                                background:
                                  item.status === "success"
                                    ? "rgba(34,197,94,0.2)"
                                    : "rgba(239,68,68,0.2)",
                                color:
                                  item.status === "success"
                                    ? theme.success
                                    : theme.danger,
                                fontWeight: 500,
                                padding: "0.25rem 0.5rem",
                                borderRadius: 6,
                                fontSize: "0.8125rem",
                              }}
                            >
                              {item.status || "—"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {totalPages > 1 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 20,
                      padding: "20px 24px",
                      borderTop: `1px solid ${theme.border}`,
                    }}
                  >
                    <div className="small" style={{ color: theme.textMuted }}>
                      Page {pagination.page} of {totalPages}
                      {pagination.total != null &&
                        ` · ${pagination.total} total`}
                    </div>
                    <ul
                      className="mb-0 list-unstyled d-flex align-items-center"
                      style={{ gap: 12 }}
                    >
                      <li>
                        <button
                          type="button"
                          className="border-0 rounded"
                          disabled={page <= 1}
                          onClick={() =>
                            page > 1 && setPage((p) => Math.max(1, p - 1))
                          }
                          style={{
                            background: theme.inputBg,
                            color: page <= 1 ? theme.textMuted : theme.text,
                            border: `1px solid ${theme.border}`,
                            padding: "0.5rem 0.75rem",
                            fontSize: "0.875rem",
                            cursor: page <= 1 ? "not-allowed" : "pointer",
                            opacity: page <= 1 ? 0.6 : 1,
                          }}
                        >
                          <FaChevronLeft
                            className="me-2"
                            style={{ fontSize: "0.75rem" }}
                          />{" "}
                          Prev
                        </button>
                      </li>
                      {Array.from(
                        { length: Math.min(5, totalPages) },
                        (_, i) => {
                          let p;
                          if (totalPages <= 5) p = i + 1;
                          else if (page <= 3) p = i + 1;
                          else if (page >= totalPages - 2)
                            p = totalPages - 4 + i;
                          else p = page - 2 + i;
                          const isActive = p === page;
                          return (
                            <li key={p}>
                              <button
                                type="button"
                                className="border-0 rounded"
                                onClick={() => setPage(p)}
                                style={{
                                  background: isActive
                                    ? theme.accent
                                    : theme.inputBg,
                                  color: isActive ? "#0f0f12" : theme.text,
                                  border: `1px solid ${isActive ? theme.accent : theme.border}`,
                                  minWidth: 36,
                                  height: 36,
                                  fontSize: "0.875rem",
                                  fontWeight: 500,
                                  cursor: "pointer",
                                }}
                              >
                                {p}
                              </button>
                            </li>
                          );
                        },
                      )}
                      <li>
                        <button
                          type="button"
                          className="border-0 rounded"
                          disabled={page >= totalPages}
                          onClick={() =>
                            page < totalPages &&
                            setPage((p) => Math.min(totalPages, p + 1))
                          }
                          style={{
                            background: theme.inputBg,
                            color:
                              page >= totalPages ? theme.textMuted : theme.text,
                            border: `1px solid ${theme.border}`,
                            padding: "0.5rem 0.75rem",
                            fontSize: "0.875rem",
                            cursor:
                              page >= totalPages ? "not-allowed" : "pointer",
                            opacity: page >= totalPages ? 0.6 : 1,
                          }}
                        >
                          Next{" "}
                          <FaChevronRight
                            className="ms-2"
                            style={{ fontSize: "0.75rem" }}
                          />
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </>
            )}
          </Card>
        </div>
      </Container>
      <style>{`
        .treasury-activity-row:hover { background: rgba(255,255,255,0.04) !important; }
      `}</style>
    </>
  );
};

export default TreasuryActivity;
